<template>
  <div class="mb-5 pb-5">
    <ValidationObserver ref="analysis-general-info">
      <AssistantInformation
        :assistants="model.districtAssistants"
        :collections="collections"
        :submitted="readOnly"
        :has-assistant-available.sync="model.districtHasAssistantsAvailable"
        :validate.sync="invalidAssistant"
        title="Información General"
        question-text="¿La escuela tiene disponible el recurso solicitado?"
      >
        <template v-slot:reason>
          <div class="row p-3">
            <div class="col-12 mt-2">
              <label class="input-label">Explique:</label>
              <ValidationProvider
                v-slot="{ errors }"
                name="teacherReason"
                :rules="'required'"
              >
                <CustomTextArea
                  id="teacherReason"
                  v-model="model.teacherReason"
                  style="width:100%"
                  :maxlength="3000"
                  :classes="['eq-field', 'text-right']"
                  :rows="5"
                  :error="errors[0]"
                  :custom-error-msg="'Favor escriba una descripción.'"
                  show-char-count
                  :disabled="readOnly"
                />
              </ValidationProvider>
            </div>
          </div>
        </template>
      </AssistantInformation>
    </ValidationObserver>
    <template v-if="model.districtHasAssistantsAvailable">
      <DocumentsForm
        class="mt-5"
        :documents="model.teacherDocuments"
        :allowed-document-types="allowedDocumentTypes"
        :multiple-uploads-document-type-ids="[requestDocumentTypeCodes.HROtro]"
        :read-only="readOnly"
      />
    </template>
    <template v-if="model.districtHasAssistantsAvailable != null">
      <ValidationObserver ref="signature-information-form">
        <TeacherFacilitatorSignature
          ref="signatureFormRef"
          v-if="canShowSignaturePanel"
          title="Firma del Facilitador Docente"
          principal-button-label="Presione aquí para añadir firma"
          role-label="Puesto"
          class="mt-5"
          :allow-add-more="allowAddSignature"
          :limit-new-signatures-to="1"
          :cannot-edit-name="true"
          :keep-signed="true"
          :use-role="true"
          :requesters="model.signersTeacher"
          :submitted="readOnly"
          :validate.sync="invalidSignature"
        />
      </ValidationObserver>
    </template>
  </div>
</template>

<script>
import CustomTextArea from 'CustomTextArea';
import { ValidationProvider } from 'vee-validate';
import alert from '@/mixin/alert.mixin';
import requestDocumentTypeCodes from '@/utils/constants/hrrequest/requestDocumentTypeCodes';
import TeacherFacilitatorSignature from '@/components/pei/ubication-tech-assistance/_components/Requesters';
import DocumentsForm from '@/views/humanResources/request/components/DocumentsForm';
import AssistantInformation from '@/views/humanResources/request/components/tabs/components/shared/AssistantInformation';
import { saveTeacherAnalysis } from '@/modules/human-resources/api';
import formDataUtil from '@/utils/formDataUtil';

export default {
  name: 'Tab3TeacherFacilitatorAnalysis',
  mixins: [alert],
  components: {
    ValidationProvider,
    CustomTextArea,
    AssistantInformation,
    DocumentsForm,
    TeacherFacilitatorSignature,
  },
  data() {
    return {
      requestDocumentTypeCodes,
      invalidAssistant: false,
      invalidSignature: false,      
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    collections: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    canShowSignaturePanel: {
      type: Boolean,      
      default: true,
    }
  },
  computed: {
    allowedDocumentTypes() {
      const documentTypeIds = [
        requestDocumentTypeCodes.HRMinuta,
        requestDocumentTypeCodes.HROtro,
      ];
      return this.collections.documentTypes.filter((docType) =>
        documentTypeIds.includes(docType.id)
      );
    },
    allowAddSignature() {
      return this.model.signersTeacher.every((x) => x.isSubmitted);
    },
    model() {
      return this.value;
    },    
  },
  methods: {
    getTeacherAnalysisFormData(submitted) {
      const formData = new FormData();

      formDataUtil.build(formData, this.model.id, 'requestId');
      formDataUtil.build(
        formData,
        this.model.districtHasAssistantsAvailable,
        'districtHasAssistantsAvailable'
      );
      if (this.model.districtHasAssistantsAvailable) {
        if (this.model.districtAssistants.length)
          formDataUtil.build(
            formData,
            this.model.districtAssistants.map((assistant) => ({
              id: assistant.id,
              assistantName: assistant.assistantName,
              dailyShiftId: assistant.dailyShift.id,
              resourceTypeId: assistant.resourceType.id,
            })),
            'districtAssistants'
          );
        formDataUtil.build(formData, this.model.teacherDocuments, 'documents');
      } else
        formDataUtil.build(formData, this.model.teacherReason, 'teacherReason');

      if (this.model.signersTeacher.length) {
        formDataUtil.build(
          formData,
          this.model.signersTeacher.map((signer) => ({
            id: signer.id || 0,
            requestId: this.model.id,
            fullName: signer.name,
            rolId: signer.role.id,
            telephoneNumber: signer.phone,
            email: signer.email,
            signed: signer.signed,
            isSubmitted: signer.isSubmitted || !!submitted,
          })),
          'signatures'
        );
      }
      return formData;
    },
    async save(submitted) {
      const isValid =
        this.model.districtHasAssistantsAvailable == null ||
        (await this.$refs['signature-information-form'].validate());

      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor de completar la información solicitada para continuar.',
          'error'
        );
        return;
      }

      await saveTeacherAnalysis(this.getTeacherAnalysisFormData(submitted))
        .then(() => {
          this.ShowToast(
            'Confirmación',
            'El análisis ha sido guardado exitosamente.',
            'success'
          );
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    async onValidateSignature() {
      if (this.model.districtHasAssistantsAvailable == null) {
        this.invalidAssistant = true;
        return false;
      }

      const formValidated = await this.$refs[
        'signature-information-form'
      ].validate();
      this.invalidSignature = this.canShowSignaturePanel && !this.$refs.signatureFormRef.hasAnyItems;
      this.invalidAssistant =
        this.model.districtHasAssistantsAvailable &&
        !this.model.districtAssistants.length;

      return formValidated && !this.invalidSignature && !this.invalidAssistant;
    },
  },
  mounted() {},
};
</script>

<style scoped></style>
